<template>
  <div
    :id="`savedSearch-${savedSearchItem.id}`"
    :style="{ color: savedSearchItem.color }"
    class="group relative rounded-full px-2 py-1.5 text-sm"
    data-testid="savedSearchListItem">
    <div
      :class="
        cn(
          'pointer-events-none absolute inset-0 size-full rounded-full bg-current px-2 py-1.5 opacity-0 transition group-hover:opacity-10',
          {
            'opacity-10': menuIsOpen || savedSearchItem.selected,
          },
        )
      "></div>
    <button class="flex w-full items-center" @click="handleItemClick">
      <UiIcon class="shrink-0" name="saved-search-filled" size="sm" />
      <span
        data-testid="savedSearchName"
        class="mr-2 ml-4 truncate text-neutral-primary">
        {{ savedSearchItem.name }}
      </span>
      <Popover
        :has-close-icon="false"
        :class="
          cn(
            'ml-auto transition-opacity',
            menuIsOpen
              ? 'opacity-100'
              : 'opacity-100 sm:opacity-0 sm:group-hover:opacity-100',
          )
        "
        @popover-opened="popoverOpened"
        @popover-closed="popoverClosed">
        <template #trigger="{ togglePopover }">
          <UiButton
            data-testid="moreMenuSavedSearch"
            appearance="ghost"
            size="xs"
            @click.stop="togglePopover">
            <UiIcon name="more-horizontal" />
          </UiButton>
        </template>
        <template #content="{ closePopover }">
          <UiListItem
            data-testid="savedSearchRename"
            @list-item-click="
              closePopover();
              handleRename();
            ">
            {{ t("control.rename") }}
          </UiListItem>
          <UiListItem
            data-testid="savedSearchNotifications"
            @list-item-click="
              closePopover();
              handleNotification();
            ">
            {{ t("control.notification") }}
          </UiListItem>
          <UiListItem
            data-testid="savedSearchDelete"
            @list-item-click="
              closePopover();
              handleDelete();
            ">
            {{ t("control.delete") }}
          </UiListItem>
        </template>
      </Popover>
    </button>
  </div>
  <DpaNewsDeleteSearchModal
    ref="deleteModal"
    :search-id="savedSearchItem.id"
    :search-name="savedSearchItem.name"
    :search-color="savedSearchItem.color"
    @deleted="emit('searchDeleted')" />
  <DpaNewsRenameSavedSearchModal
    ref="renameModal"
    :search-color="savedSearchItem.color"
    :search-id="savedSearchItem.id"
    :search-name="savedSearchItem.name"
    @edited="emit('searchEdited')" />
  <DpaNewsNotifySavedSearchModal
    ref="notifyModal"
    :search-id="savedSearchItem.id"
    :search-notify-by-app="
      savedSearchItem.notificationSettings.notifyByDpaIdPush
    "
    :search-notify-by-email="savedSearchItem.notificationSettings.notifyByEmail"
    @edited="emit('searchEdited')" />
</template>
<script lang="ts" setup>
import UiButton from "@dpa-id-components/dpa-shared-components/src/components/UiButton/UiButton.vue";
import UiIcon from "@dpa-id-components/dpa-shared-components/src/components/UiIcon/UiIcon.vue";
import UiListItem from "@dpa-id-components/dpa-shared-components/src/components/UiListItem/UiListItem.vue";
import { cn } from "@dpa-id-components/dpa-shared-components/src/utils/cn.ts";
import { ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import Popover from "@/components/shared/Popover/Popover.vue";
import { useTracking } from "@/composition/shared/useTracking.ts";
import { useLayoutStore } from "@/stores/layout.ts";
import useSavedSearchesStore from "@/stores/search/savedSearches.ts";
import type { SavedSearchItem } from "@/types/savedSearch";
import { savedSearchApiClient } from "@/utils/apiClients.ts";
import { handleLocalError } from "@/utils/errorHandling.ts";

import DpaNewsDeleteSearchModal from "../DpaNewsSavedSearches/DpaNewsDeleteSearchModal.vue";
import DpaNewsNotifySavedSearchModal from "../DpaNewsSavedSearches/DpaNewsNotifySavedSearchModal.vue";
import DpaNewsRenameSavedSearchModal from "../DpaNewsSavedSearches/DpaNewsRenameSavedSearchModal.vue";

const { t } = useI18n();
const savedSearchesStore = useSavedSearchesStore();

const { savedSearchItem } = defineProps<{
  savedSearchItem: SavedSearchItem;
}>();

const menuIsOpen = ref(false);

const { trackEvent } = useTracking();
const layoutStore = useLayoutStore();

const deleteModal =
  useTemplateRef<InstanceType<typeof DpaNewsDeleteSearchModal>>("deleteModal");
const renameModal =
  useTemplateRef<InstanceType<typeof DpaNewsRenameSavedSearchModal>>(
    "renameModal",
  );
const notifyModal =
  useTemplateRef<InstanceType<typeof DpaNewsNotifySavedSearchModal>>(
    "notifyModal",
  );

async function handleItemClick() {
  try {
    // Try to fetch the saved search from the backend
    const { error } = await savedSearchApiClient(`/${savedSearchItem.id}`)
      .get()
      .json();

    if (error.value) {
      handleLocalError(error.value.status, t("errors.saveSearchLoad.failed"));

      return; // Do NOT apply filters
    }

    // Only apply filters if fetch succeeded
    savedSearchesStore.setSelectedSearch(
      savedSearchItem.filterRules,
      savedSearchItem.id,
    );
  } catch (e) {
    void e;
    layoutStore.addSnackbarMessage(
      t("errors.saveSearchLoad.failed"),
      "error",
      "alert-filled",
    );
  }
}

function popoverOpened() {
  menuIsOpen.value = true;
  trackEvent(
    "saveSearch:moreMenu_click_iconButton_openMoreMenu",
    "more-horizontal",
  );
  emit("moreMenuOpened");
}
function popoverClosed() {
  menuIsOpen.value = false;
  emit("moreMenuClosed");
}

function handleDelete() {
  trackEvent(
    "saveSearch:moreMenu_click_listItem_selectMoreMenu",
    t("control.delete"),
  );
  deleteModal.value?.showModal();
}

function handleRename() {
  trackEvent(
    "saveSearch:moreMenu_click_listItem_selectMoreMenu",
    t("control.rename"),
  );
  renameModal.value?.showModal();
}

function handleNotification() {
  trackEvent(
    "saveSearch:moreMenu_click_listItem_selectMoreMenu",
    t("control.notification"),
  );
  notifyModal.value?.showModal();
}

const emit = defineEmits<{
  savedSearchClicked: [];
  searchDeleted: [];
  searchEdited: [];
  moreMenuOpened: [];
  moreMenuClosed: [];
}>();
</script>
